import { Link, useSearchParams } from 'react-router-dom'
import SiteLayout from '@/components/feature/SiteLayout'
import MovieCard from '@/components/feature/MovieCard'
import Pagination from '@/components/feature/Pagination'
import { usePageSeo } from '@/hooks/usePageSeo'
import { categories, movies } from '@/mocks/movies'
import FilterBar from './components/FilterBar'

const PAGE_SIZE = 8

const typeTitles: Record<string, string> = {
  all: '全部影视作品',
  movie: '电影',
  tv: '电视剧',
  short: '短片',
}

export default function Category() {
  const [params, setParams] = useSearchParams()

  const type = params.get('type') ?? 'all'
  const genre = params.get('genre') ?? 'all'
  const year = params.get('year') ?? 'all'
  const sort = params.get('sort') ?? 'latest'
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1)

  const typeLabel = typeTitles[type] ?? '全部影视作品'
  const pageTitle = `麻豆传媒在线 - ${typeLabel} - 麻豆传媒影视作品大全`
  const pageDescription = `麻豆传媒在线${typeLabel}分类页面，按题材类型、上映年份与热度整理影视作品，方便快速筛选并查看剧情简介、导演与演员信息。`
  const pageKeywords = `麻豆传媒在线,${typeLabel},影视作品大全,影视分类,在线影视,影片筛选`

  usePageSeo(pageTitle, pageDescription, pageKeywords)

  const updateParam = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params)
    Object.entries(patch).forEach(([key, value]) => {
      if (!value || value === 'all' || (key === 'sort' && value === 'latest')) {
        next.delete(key)
      } else {
        next.set(key, value)
      }
    })
    next.delete('page')
    setParams(next)
  }

  const resetFilters = () => {
    const next = new URLSearchParams()
    if (type !== 'all') next.set('type', type)
    setParams(next)
  }

  const filtered = movies
    .filter((movie) => (type === 'all' ? true : movie.category === type))
    .filter((movie) => (genre === 'all' ? true : movie.genres.includes(genre)))
    .filter((movie) => (year === 'all' ? true : String(movie.year) === year))
    .sort((a, b) => {
      if (sort === 'hot') return a.hot - b.hot
      return b.year - a.year || a.hot - b.hot
    })

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  const changePage = (nextPage: number) => {
    const next = new URLSearchParams(params)
    if (nextPage <= 1) {
      next.delete('page')
    } else {
      next.set('page', String(nextPage))
    }
    setParams(next)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const categoryHint =
    type === 'all'
      ? '按分类浏览全站示例影视作品，可按题材、年份与热度进一步筛选。'
      : `${typeTitles[type] ?? ''}分类下的示例作品集合，打开任意作品可查看完整剧情简介与演职人员信息。`

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="page-shell py-8 md:py-10">
          <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-accent-700">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400" />
            <Link to="/category" className="transition-colors hover:text-accent-700">
              影视分类
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400" />
            <span className="text-foreground-800">{typeLabel}</span>
          </nav>

          <h1 className="mt-4 text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
            {typeLabel}
            <span className="ml-2 text-sm font-normal text-foreground-500">
              共 {filtered.length} 部
            </span>
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-foreground-600">{categoryHint}</p>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <Link
              to="/category"
              className={`whitespace-nowrap rounded-md border px-4 py-2 text-xs transition-colors ${
                type === 'all'
                  ? 'border-primary-800 bg-primary-800 text-background-50'
                  : 'border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300'
              }`}
            >
              全部
            </Link>
            {categories.map((item) => (
              <Link
                key={item.key}
                to={`/category?type=${item.key}`}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-md border px-4 py-2 text-xs transition-colors ${
                  type === item.key
                    ? 'border-primary-800 bg-primary-800 text-background-50'
                    : 'border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300'
                }`}
              >
                <i className={item.icon} />
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="page-shell py-8 md:py-10">
        <FilterBar
          value={{ genre, year, sort }}
          onChange={(patch) => updateParam(patch)}
          onReset={resetFilters}
        />

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-xs text-foreground-500">
          <p>
            筛选结果 <span className="font-medium text-foreground-900">{filtered.length}</span> 部 ·
            第 {currentPage} / {totalPages} 页
          </p>
          <p className="hidden items-center gap-1 sm:flex">
            <i className="ri-information-line" />
            点击卡片可进入详情页查看剧情与演职人员
          </p>
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="mt-5 grid grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-3 md:gap-x-4 lg:grid-cols-4">
              {pageItems.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              onChange={changePage}
            />
          </>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center rounded-xl border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-inbox-line text-2xl" />
            </span>
            <h2 className="mt-4 text-base font-medium text-foreground-900">暂时没有符合条件的作品</h2>
            <p className="mt-2 max-w-sm text-xs leading-6 text-foreground-500">
              当前筛选组合下没有匹配结果，试试放宽类型或年份条件，也可以直接浏览全部作品。
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={resetFilters}
                className="whitespace-nowrap rounded-md bg-primary-800 px-5 py-2.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                重置筛选条件
              </button>
              <Link
                to="/category"
                className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-800"
              >
                浏览全部作品
              </Link>
            </div>
          </div>
        )}
      </div>
    </SiteLayout>
  )
}